import React, { useEffect, useRef, useState } from 'react';
import { useAddEmail } from '../../../hooks/apolloHooks/useAddEmail';
import { useResendEmailVerificationCode } from '../../../hooks/apolloHooks/useResendEmailVerificationCode';
import { useUpdateEmail } from '../../../hooks/apolloHooks/useUpdateEmail';
import { useVerifyEmail } from '../../../hooks/apolloHooks/useVerifyEmail';
import { validateEmail } from '../../../utils/contactValidationSchemas';
import { CloseIcon, InfoIconOutlined } from '../../svgs/ExpressonSVGs';
import Button from '../Button';
import Tooltip from '../Tooltip';

type ProfileDetailsEditMailProps = {
	currentEmail: string
	currentEmailId: number | undefined
	currentEmailVerified: boolean
	customerId: number
	onUpdateSuccess?: (newEmail?: string) => void
	handleCancel: () => void
}

const ProfileDetailsEditMail: React.FC<ProfileDetailsEditMailProps> = ({
	currentEmail,
	currentEmailId,
	currentEmailVerified,
	customerId,
	onUpdateSuccess,
	handleCancel,
}) => {
	const isUnverifiedExisting = !!currentEmailId && !currentEmailVerified

	const [emailValue, setEmailValue] = useState(currentEmail || '')
	const [emailError, setEmailError] = useState<string | undefined>(undefined)
	const [isEmailValid, setIsEmailValid] = useState(() => validateEmail(currentEmail || '').valid)

	const [emailVerificationSent, setEmailVerificationSent] = useState(false)
	const [isEmailInputDisabled, setIsEmailInputDisabled] = useState(isUnverifiedExisting)
	const [sentEmailValue, setSentEmailValue] = useState<string>(currentEmail || '')
	const [verifiedEmail, setVerifiedEmail] = useState<string | null>(null)
	const [addedEmailId, setAddedEmailId] = useState<number | null>(
		currentEmailId ?? null
	)
	const [codeValue, setCodeValue] = useState('')
	const [submitError, setSubmitError] = useState<string | null>(null)
	const [cooldownSeconds, setCooldownSeconds] = useState(0)

	const emailInputRef = useRef<HTMLInputElement>(null)
	const codeInputRef = useRef<HTMLInputElement>(null)
	const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null)

	const { addEmail, loading: addLoading } = useAddEmail()
	const { updateEmail, loading: updateLoading } = useUpdateEmail()
	const { verifyEmail, loading: verifyLoading } = useVerifyEmail()
	const { resendCode } = useResendEmailVerificationCode()

	const loading = addLoading || updateLoading

	// Auto-focus code input when verification step appears
	useEffect(() => {
		if (emailVerificationSent) {
			setTimeout(() => codeInputRef.current?.focus(), 0)
		}
	}, [emailVerificationSent])

	// Cleanup cooldown interval on unmount
	useEffect(() => {
		return () => { if (cooldownRef.current) clearInterval(cooldownRef.current) }
	}, [])

	const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value
		setEmailValue(value)
		const result = validateEmail(value)
		setIsEmailValid(result.valid)
		setEmailError(result.valid ? undefined : result.error)
	}

	const handleSendVerification = async () => {
		const result = validateEmail(emailValue)
		if (!result.valid || !customerId) return
		setSubmitError(null)
		try {
			if (addedEmailId) {
				// Existing email — resend or update
				if (emailValue === sentEmailValue) {
					if (currentEmailVerified) {
						// Already verified with same address — nothing to do
						return
					}
					// Unverified same address — resend the verification code
					await resendCode({ emailId: addedEmailId })
				} else {
					// Different email — update also triggers a new verification code
					await updateEmail({
						emailId: addedEmailId,
						customerId,
						address: emailValue,
						emailType: 'MAIN',
						sortOrder: 1,
					})
					setSentEmailValue(emailValue)
				}
				setEmailVerificationSent(true)
				setIsEmailInputDisabled(true)
			} else {
				// No existing email — add new
				const added = await addEmail({
					address: emailValue,
					customerId,
					emailType: 'MAIN',
				})
				if (added) {
					setEmailVerificationSent(true)
					setAddedEmailId(added[0]?.id ?? null)
					setSentEmailValue(emailValue)
				}
			}
		} catch (err: any) {
			console.error('[ProfileDetailsEditMail] send verification failed:', err)
			setSubmitError('Något gick fel. Försök igen.')
		}
	}

	const handleEditEmail = () => {
		setEmailVerificationSent(false)
		setIsEmailInputDisabled(false)
		setCodeValue('')
		setSubmitError(null)
		setCooldownSeconds(0)
		if (cooldownRef.current) {
			clearInterval(cooldownRef.current)
			cooldownRef.current = null
		}
		setTimeout(() => emailInputRef.current?.focus(), 0)
	}

	const handleCodeVerification = async () => {
		if (!codeValue || !addedEmailId) return
		setSubmitError(null)
		try {
			const verified = await verifyEmail({
				emailId: addedEmailId,
				verificationCode: codeValue,
			})
			console.log('[ProfileDetailsEditMail] Verify result:', verified)
			if (verified) {
				const verEmail = verified[0]?.address ?? emailValue
				setVerifiedEmail(verEmail)
				setTimeout(() => onUpdateSuccess?.(verEmail), 5000)
			}
		} catch (err: any) {
			console.error('[ProfileDetailsEditMail] verifyEmail failed:', err)
			setSubmitError('Felaktig verifieringskod. Kontrollera koden och försök igen.')
			setCodeValue('')
		}
	}

	const handleResend = async () => {
		if (!addedEmailId || cooldownSeconds > 0) return
		try {
			await resendCode({ emailId: addedEmailId })
		} catch (err) {
			console.error('[ProfileDetailsEditMail] resendCode failed:', err)
		}
		setCooldownSeconds(30)
		cooldownRef.current = setInterval(() => {
			setCooldownSeconds(prev => {
				if (prev <= 1) {
					clearInterval(cooldownRef.current!)
					return 0
				}
				return prev - 1
			})
		}, 1000)
	}

	const isVerified = verifiedEmail !== null

	return (
		<>
			<div className="account-email-edit">
				{/* Success message — shown after verification */}
				{isVerified ? (
					<div className="verification-success">
						<span>E-postadressen har verifierats!</span>
					</div>
				) : (
					<>
						{/* Email input */}
						<div className="input-wrapper">
							<label htmlFor="profile-email">E-postadress</label>
							<input
								ref={emailInputRef}
								type="email"
								id="profile-email"
								name="email"
								placeholder="Ange din e-postadress"
								value={isVerified ? verifiedEmail : emailValue}
								disabled={emailVerificationSent || isEmailInputDisabled}
								onChange={handleEmailChange}
							/>
							{emailError && emailValue.length > 0 && (
								<span className="input-error">{emailError}</span>
							)}
							{!emailVerificationSent && !isVerified && (
								<span>Vi skickar en verifieringskod till denna adress.</span>
							)}
						</div>

						{/* Verification code input — shown after email submitted */}
						{emailVerificationSent && !isVerified && (
							<div className="input-wrapper verification">
								<label htmlFor="profile-verificationCode">
									Verifieringskod
									<Tooltip
										position="top-end"
										text="Verifieringskoden är giltig i 10 dagar. Om du inte har fått någon kod, kontrollera din e-post du skrivit in, skräppostmapp eller klicka på 'Skicka verifieringskod igen'."
									>
										<InfoIconOutlined width={25} height={26} />
									</Tooltip>
								</label>
								<input
									ref={codeInputRef}
									type="text"
									inputMode="numeric"
									pattern="[0-9]*"
									id="profile-verificationCode"
									name="verificationCode"
									placeholder="Ange verifieringskoden"
									maxLength={4}
									value={codeValue}
									onChange={(e) => setCodeValue(e.target.value.replace(/\D/g, ''))}
								/>
								<span>Fyll i koden du fick i mejlet för att verifiera din e-postadress.</span>
								<span
									className={`resend-verification${cooldownSeconds > 0 ? ' resend-verification--cooldown' : ''}`}
									onClick={handleResend}
									tabIndex={cooldownSeconds > 0 ? -1 : 0}
									onKeyDown={(e) => { if (cooldownSeconds === 0 && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); handleResend(); } }}
								>
									{cooldownSeconds > 0
										? `Skicka igen om ${cooldownSeconds}s`
										: 'Skicka verifieringskod igen'
									}
								</span>
							</div>
						)}

						{submitError && (
							<span className="input-error">{submitError}</span>
						)}

						{/* Action buttons */}
						<div className="button-wrapper">
							{!emailVerificationSent ? (
								<>
									<button
										type="button"
										className="button-edit body-text"
										disabled={!isEmailValid || loading || (currentEmailVerified && emailValue === sentEmailValue)}
										onClick={handleSendVerification}
									>
										{loading ? 'Skickar...' : 'Skicka verifieringskod'}
									</button>
									{isEmailInputDisabled && (
										<button
											type="button"
											className="button-edit body-text"
											onClick={handleEditEmail}
										>
											Redigera e-postadress
										</button>
									)}
								</>
							) : (
								<>
									<button
										type="button"
										className="button-edit body-text"
										disabled={codeValue.length !== 4 || verifyLoading}
										onClick={handleCodeVerification}
									>
										{verifyLoading ? 'Verifierar...' : 'Verifiera e-post'}
									</button>
									<button
										type="button"
										className="button-edit body-text"
										disabled={codeValue.length === 4}
										onClick={handleEditEmail}
									>
										Redigera e-postadress
									</button>
								</>
							)}
						</div>
					</>
				)}
			</div>

			{/* Close button — hidden while verification is in progress or done */}
			{!emailVerificationSent && !isVerified && (
				<Button
					type="button"
					className="button-close white-bg"
					onClick={handleCancel}
				>
					<CloseIcon />
				</Button>
			)}
		</>
	)
}

export default ProfileDetailsEditMail
